// src/components/notify/console/NotifyComposer.js
// Compose → preview (FlowSense AI forecast) → send / schedule.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Box, Button, ButtonGroup, Checkbox, Flex, FormControl, FormHelperText,
    FormLabel, Grid, GridItem, Heading, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
    ModalHeader, ModalOverlay, Select, SimpleGrid, Stack, Switch, Text, Textarea, Tooltip, useColorModeValue,
    useDisclosure, useToast,
} from '@chakra-ui/react';
import { MdSchedule, MdSend } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import NotifyAudienceBuilder, { EMPTY_AUDIENCE, audienceIsEmpty, audienceToSpec } from './NotifyAudienceBuilder';
import NotifyForecastPanel from './NotifyForecastPanel';
import { CATEGORY_META, PRIORITY_META } from '../notifyFormat';

const ACK_TIMEOUTS = [
    { label: '5 minutes', sec: 300 }, { label: '15 minutes', sec: 900 }, { label: '30 minutes', sec: 1800 },
    { label: '1 hour', sec: 3600 }, { label: '4 hours', sec: 14400 }, { label: 'Never escalate', sec: 0 },
];
const EXPIRY = [{ label: 'Never', hours: 0 }, { label: '1 day', hours: 24 }, { label: '3 days', hours: 72 }, { label: '1 week', hours: 168 }];
const CHANNEL_OPTIONS = [
    { key: 'realtime', label: 'Live (web + tech app)' },
    { key: 'webpush', label: 'Browser push' },
    // --- UPDATE (FCM channel)
    { key: 'fcm', label: 'Mobile push (app asleep)' },
    // --- END UPDATE
    { key: 'email', label: 'Email' },
];
const newKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const toLocalInput = (d) => {
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const INITIAL = {
    template: '', vars: {}, title: '', body: '', category: 'announcements', priority: 'normal',
    channels: ['realtime', 'webpush', 'fcm'], requiresAck: false, ackTimeoutSec: 900, actionUrl: '',
    schedule: false, scheduleAt: '', expiryHours: 0,
};

export default function NotifyComposer({ permissions = {}, templates = [], presetTemplate = null, onSent }) {
    const toast = useToast();
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const confirm = useDisclosure();
    const [audience, setAudience] = useState(EMPTY_AUDIENCE);
    const [form, setForm] = useState(INITIAL);
    const [preview, setPreview] = useState({ data: null, loading: false, error: null });
    const [sending, setSending] = useState(false);
    const idemKey = useRef(newKey());
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
    const tpl = useMemo(() => templates.find(t => String(t.key) === String(form.template) || String(t.id) === String(form.template)) || null, [templates, form.template]);

    const applyTemplate = useCallback((t) => {
        if (!t) { setForm(f => ({ ...f, template: '', vars: {} })); return; }
        setForm(f => ({
            ...f,
            template: t.system ? t.key : String(t.id),
            title: t.title,
            body: t.body,
            category: t.category || f.category,
            priority: t.priority === 'critical' && !permissions.critical ? 'high' : (t.priority || f.priority),
            requiresAck: Boolean(t.requires_ack),
            actionUrl: t.action_url || '',
            vars: Object.fromEntries((t.variables || []).map(v => [v, f.vars[v] || ''])),
        }));
    }, [permissions.critical]);

    useEffect(() => { if (presetTemplate) applyTemplate(presetTemplate); }, [presetTemplate, applyTemplate]);

    const varsObject = useMemo(() => {
        // "workOrder.title" → { workOrder: { title } }
        const out = {};
        Object.entries(form.vars).forEach(([path, val]) => {
            const parts = path.split('.');
            let cur = out;
            parts.forEach((p, i) => { if (i === parts.length - 1) cur[p] = val; else { cur[p] = cur[p] || {}; cur = cur[p]; } });
        });
        return out;
    }, [form.vars]);

    const payload = useMemo(() => {
        const scheduleAt = form.schedule && form.scheduleAt ? new Date(form.scheduleAt).toISOString() : undefined;
        return {
            to: audienceToSpec(audience),
            title: form.title,
            body: form.body,
            template: form.template || undefined,
            vars: varsObject,
            category: form.category,
            priority: form.priority,
            channels: ['inapp', ...form.channels],
            requiresAck: form.requiresAck,
            ackTimeoutSec: form.requiresAck ? form.ackTimeoutSec : undefined,
            actionUrl: form.actionUrl.trim() || undefined,
            scheduleAt,
            expiresAt: form.expiryHours ? new Date(Date.now() + form.expiryHours * 3600000).toISOString() : undefined,
        };
    }, [audience, form, varsObject]);

    // Debounced live preview + forecast
    useEffect(() => {
        if (audienceIsEmpty(audience) || !form.title.trim()) { setPreview({ data: null, loading: false, error: null }); return undefined; }
        const ctrl = new AbortController();
        setPreview(p => ({ ...p, loading: true, error: null }));
        const t = setTimeout(async () => {
            try {
                const data = await api.previewNotification(payload, ctrl.signal);
                if (isMounted.current && !ctrl.signal.aborted) setPreview({ data, loading: false, error: null });
            } catch (e) {
                if (e?.name !== 'AbortError' && isMounted.current) setPreview(p => ({ ...p, loading: false, error: e }));
            }
        }, 650);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [payload, audience, form.title]);

    const recipients = preview.data?.recipients ?? null;
    const needsConfirm = form.priority === 'critical' || audience.all || (recipients !== null && recipients > 100);
    const invalidSchedule = form.schedule && (!form.scheduleAt || new Date(form.scheduleAt).getTime() < Date.now() + 60000);
    const canSend = !audienceIsEmpty(audience) && form.title.trim() && form.body.trim() && recipients !== 0 && !invalidSchedule && !sending;

    const doSend = useCallback(async () => {
        confirm.onClose();
        setSending(true);
        try {
            const r = await api.sendNotification({ ...payload, idempotencyKey: idemKey.current });
            const d = r.data || {};
            toast({
                status: 'success',
                title: d.status === 'scheduled' ? 'Notification scheduled' : d.status === 'deduped' ? 'Already sent' : 'Notification sent',
                description: d.status === 'scheduled' ? `Goes out ${new Date(payload.scheduleAt).toLocaleString()}` : `${recipients ?? ''} recipient(s) — track it in History.`,
                duration: 4000,
            });
            idemKey.current = newKey();
            if (isMounted.current) { setForm(INITIAL); setAudience(EMPTY_AUDIENCE); }
            onSent?.(d);
        } catch (e) {
            toast({ status: 'error', title: 'Could not send', description: e.message });
        } finally {
            if (isMounted.current) setSending(false);
        }
    }, [payload, recipients, toast, onSent, confirm]);

    const submit = useCallback(() => { if (needsConfirm) confirm.onOpen(); else doSend(); }, [needsConfirm, confirm, doSend]);
    const toggleChannel = (k) => set('channels', form.channels.includes(k) ? form.channels.filter(c => c !== k) : [...form.channels, k]);

    return (
        <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1.6fr) minmax(320px, 1fr)' }} gap={4}>
            <GridItem>
                <Stack spacing={4}>
                    <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                        <Heading size="sm" mb={3}>1 · Who</Heading>
                        <NotifyAudienceBuilder value={audience} onChange={setAudience} canEveryone={permissions.everyone} recipients={recipients} byRole={preview.data?.by_role} />
                    </Box>

                    <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                        <Heading size="sm" mb={3}>2 · Message</Heading>
                        <Stack spacing={3}>
                            <FormControl>
                                <FormLabel fontSize="sm">Template</FormLabel>
                                <Select size="sm" value={form.template} onChange={(e) => applyTemplate(templates.find(t => (t.system ? t.key : String(t.id)) === e.target.value))} aria-label="Template">
                                    <option value="">No template — write a message</option>
                                    <optgroup label="Built-in">
                                        {templates.filter(t => t.system).map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                                    </optgroup>
                                    {templates.some(t => !t.system && t.is_active) && (
                                        <optgroup label="Your templates">
                                            {templates.filter(t => !t.system && t.is_active).map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                                        </optgroup>
                                    )}
                                </Select>
                            </FormControl>
                            {tpl && Object.keys(form.vars).length > 0 && (
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                    {Object.keys(form.vars).map(v => (
                                        <FormControl key={v}>
                                            <FormLabel fontSize="xs" mb={0.5}>{v}</FormLabel>
                                            <Input size="sm" value={form.vars[v]} onChange={(e) => set('vars', { ...form.vars, [v]: e.target.value })} placeholder={`{{${v}}}`} />
                                        </FormControl>
                                    ))}
                                </SimpleGrid>
                            )}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Title</FormLabel>
                                <Input value={form.title} maxLength={255} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Bay St closed for main repair" />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Message</FormLabel>
                                <Textarea value={form.body} maxLength={4000} rows={4} onChange={(e) => set('body', e.target.value)} placeholder="What do people need to know or do?" />
                                <FormHelperText fontSize="xs" textAlign="right">{form.body.length}/4000</FormHelperText>
                            </FormControl>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Topic</FormLabel>
                                    <Select size="sm" value={form.category} onChange={(e) => set('category', e.target.value)} aria-label="Topic">
                                        {Object.entries(CATEGORY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Priority</FormLabel>
                                    <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                                        {Object.entries(PRIORITY_META).map(([k, m]) => {
                                            const locked = k === 'critical' && !permissions.critical;
                                            const btn = (
                                                <Button key={k} flex="1" colorScheme={form.priority === k ? m.color : 'gray'} variant={form.priority === k ? 'solid' : 'outline'}
                                                    onClick={() => set('priority', k)} isDisabled={locked} aria-pressed={form.priority === k}>{m.label}</Button>
                                            );
                                            return locked ? <Tooltip key={k} label="Only supervisors, managers and admins can send critical notifications" hasArrow><span style={{ flex: 1, display: 'flex' }}>{btn}</span></Tooltip> : btn;
                                        })}
                                    </ButtonGroup>
                                </FormControl>
                            </SimpleGrid>
                            <FormControl>
                                <FormLabel fontSize="sm">Link (optional)</FormLabel>
                                <Input size="sm" value={form.actionUrl} onChange={(e) => set('actionUrl', e.target.value)} placeholder="/admin/work-orders?workOrderId=WO-123" />
                                <FormHelperText fontSize="xs">Opened when the notification is tapped — a FlowSense path or an https:// link.</FormHelperText>
                            </FormControl>
                        </Stack>
                    </Box>

                    <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                        <Heading size="sm" mb={3}>3 · Delivery</Heading>
                        <Stack spacing={3}>
                            <HStack spacing={4} flexWrap="wrap">
                                <Checkbox isChecked isDisabled>In-app inbox</Checkbox>
                                {CHANNEL_OPTIONS.map(c => <Checkbox key={c.key} isChecked={form.channels.includes(c.key)} onChange={() => toggleChannel(c.key)}>{c.label}</Checkbox>)}
                            </HStack>
                            <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                <Box>
                                    <Text fontSize="sm" fontWeight="600">Require acknowledgement</Text>
                                    <Text fontSize="xs" color={muted}>If not acknowledged in time it escalates: technicians, contractors and inspectors → supervisor → manager.</Text>
                                </Box>
                                <HStack>
                                    {form.requiresAck && (
                                        <Select size="sm" w="170px" value={form.ackTimeoutSec} onChange={(e) => set('ackTimeoutSec', Number(e.target.value))} aria-label="Escalate after">
                                            {ACK_TIMEOUTS.map(o => <option key={o.sec} value={o.sec}>{o.sec ? `Escalate after ${o.label}` : o.label}</option>)}
                                        </Select>
                                    )}
                                    <Switch colorScheme="red" isChecked={form.requiresAck} onChange={(e) => set('requiresAck', e.target.checked)} aria-label="Require acknowledgement" />
                                </HStack>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl>
                                    <Flex justify="space-between" align="center"><FormLabel fontSize="sm" mb={0}>Schedule for later</FormLabel><Switch isChecked={form.schedule} onChange={(e) => { set('schedule', e.target.checked); if (e.target.checked && !form.scheduleAt) set('scheduleAt', toLocalInput(new Date(Date.now() + 3600000))); }} aria-label="Schedule for later" /></Flex>
                                    {form.schedule && <Input mt={2} size="sm" type="datetime-local" value={form.scheduleAt} onChange={(e) => set('scheduleAt', e.target.value)} aria-label="Send at" />}
                                    {invalidSchedule && <FormHelperText color="red.400" fontSize="xs">Pick a time at least a minute from now.</FormHelperText>}
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Remove from inboxes after</FormLabel>
                                    <Select size="sm" value={form.expiryHours} onChange={(e) => set('expiryHours', Number(e.target.value))} aria-label="Expiry">
                                        {EXPIRY.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}
                                    </Select>
                                </FormControl>
                            </SimpleGrid>
                        </Stack>
                    </Box>

                    <Flex justify="flex-end" gap={2}>
                        <Button variant="ghost" onClick={() => { setForm(INITIAL); setAudience(EMPTY_AUDIENCE); }} isDisabled={sending}>Clear</Button>
                        <Button colorScheme={form.priority === 'critical' ? 'red' : 'purple'} leftIcon={form.schedule ? <MdSchedule /> : <MdSend />} onClick={submit} isDisabled={!canSend} isLoading={sending} loadingText="Sending">
                            {form.schedule ? 'Schedule' : `Send${recipients ? ` to ${recipients}` : ''}`}
                        </Button>
                    </Flex>
                </Stack>
            </GridItem>
            <GridItem>
                <Box position={{ xl: 'sticky' }} top={{ xl: 4 }}>
                    <NotifyForecastPanel preview={preview.data} loading={preview.loading} error={preview.error} title={form.title} body={form.body} category={form.category} priority={form.priority} requiresAck={form.requiresAck} />
                </Box>
            </GridItem>

            <Modal isOpen={confirm.isOpen} onClose={confirm.onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Send {form.priority === 'critical' ? 'a critical notification' : 'this notification'}?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status={form.priority === 'critical' ? 'error' : 'warning'} borderRadius="lg" mb={3}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                {preview.data?.audience_label || 'Your audience'} — <b>{recipients ?? '?'}</b> people.
                                {form.priority === 'critical' ? ' Critical notifications break through quiet hours and mute.' : ''}
                            </AlertDescription>
                        </Alert>
                        <Text fontWeight="700">{preview.data?.title || form.title}</Text>
                        <Text fontSize="sm" color={muted} whiteSpace="pre-line">{preview.data?.body || form.body}</Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={confirm.onClose}>Cancel</Button>
                        <Button colorScheme={form.priority === 'critical' ? 'red' : 'purple'} onClick={doSend} isLoading={sending}>Yes, send</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Grid>
    );
}
